/**
 * conversation-model-versioning Phase 5 — consolidated version controls.
 *
 * Single tab-row entry point that replaces the three separate VariablesPanel
 * header controls (Versions / Create version / Accept). Mounts in
 * `<BuilderTabBar>`'s `rightSlot` on ALL tabs (Workbench/Signal/Model),
 * opposite the tab strip.
 *
 * Dropdown with three actions, each routing to its existing flow:
 *  - Browse versions  → dispatches `arcanum:open-versions` (root-mounted
 *                        `<BrowseVersionsModal>` listens — mirrors
 *                        `<VersionsTriggerButton>`'s window-event pattern).
 *  - Create version   → calls `useCreateVersion` directly. (The legacy
 *                        `arcanum:create-version` event had NO listener —
 *                        the control was dead before this consolidation.)
 *                        Gated by the ADR-0009 client completeness mirror.
 *  - Accept for        → renders `<AcceptUnderwritingButton menuItem>` (its
 *    underwriting        ConfirmModal + mutation are reused intact), gated on
 *                        the SAME readiness as Create version.
 *
 * Tooltip/disabled copy follows the project user-facing-copy rule: no
 * internal-flow nouns ("gate", "agent_state", "provenance", "pointer").
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type {
  InputDefinition,
  ProvenanceRecord,
} from '@arcanum-hq/shared-types';
import {
  computeVersionReadiness,
  type DealMetadataLike,
  type ProvenanceEntry,
} from '@/lib/version-completeness';
import { useCreateVersion } from '@/hooks/use-conversation-versions';
import { AcceptUnderwritingButton } from './AcceptUnderwritingButton';

export interface ManageVersionsButtonProps {
  conversationId: string | null;
  /** `modelDetail?.data?.inputs` — completeness denominator. */
  schemaInputs?: (Pick<InputDefinition, 'name' | 'displayName'> & {
    dataType?: InputDefinition['dataType'];
  })[];
  /**
   * `agent_state.inputProvenance` from the active conversation. Accepts both
   * the full `ProvenanceRecord` shape (VariablesPanel) and the route-scope
   * minimal `{ source?, defaultStatus? }` projection — `computeVersionReadiness`
   * normalizes string | object and only reads `source` / `defaultStatus`.
   */
  inputProvenance?: Record<
    string,
    | ProvenanceRecord
    | { source?: string; defaultStatus?: string | null }
    | string
    | undefined
  > | null;
  /** `agent_state.orphanedInputs` — non-empty blocks Create version. */
  orphanedInputs?: Record<string, unknown> | null;
  /** `agent_state.dealMetadata` — location-resolution check (ADR 0003). */
  dealMetadata?: DealMetadataLike | null;
}

/** Build the disabled tooltip for Create version / Accept (no internal nouns). */
function blockedTooltip(unaddressedNames: string[]): string {
  const locationEntries = unaddressedNames.filter(
    (n) => n === 'Property location — not yet resolved'
  );
  const regularEntries = unaddressedNames.filter(
    (n) => n !== 'Property location — not yet resolved'
  );
  const allNames = [...regularEntries, ...locationEntries];
  const preview = allNames.slice(0, 3).join(', ');
  const overflow = allNames.length > 3 ? ` (+${allNames.length - 3} more)` : '';
  return `Address these inputs first: ${preview}${overflow}`;
}

export function ManageVersionsButton({
  conversationId,
  schemaInputs,
  inputProvenance,
  orphanedInputs,
  dealMetadata,
}: ManageVersionsButtonProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const createVersion = useCreateVersion(conversationId);

  // Close on click-outside + Escape (DealSwitcherDropdown pattern).
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onKeydown);
    return () => {
      window.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onKeydown);
    };
  }, [open]);

  const hasConversation = conversationId !== null;
  const hasSchemaInputs =
    Array.isArray(schemaInputs) && schemaInputs.length > 0;

  const { ready: canCreateVersion, unaddressed } = hasSchemaInputs
    ? computeVersionReadiness(
        schemaInputs,
        (inputProvenance as unknown as Record<
          string,
          ProvenanceEntry | string | undefined
        >) ?? {},
        Object.fromEntries(
          schemaInputs
            .filter(
              (
                inp
              ): inp is typeof inp & {
                dataType: NonNullable<typeof inp.dataType>;
              } => inp.dataType != null
            )
            .map((inp) => [inp.name, inp.dataType])
        ),
        dealMetadata,
        orphanedInputs ?? {}
      )
    : { ready: false, unaddressed: [] };

  const unaddressedNames = unaddressed.map((u) =>
    u.isLocation ? 'Property location — not yet resolved' : u.displayName
  );
  const blockReason = blockedTooltip(unaddressedNames);
  const versionReady = hasConversation && canCreateVersion;
  const createDisabled =
    !hasConversation || !canCreateVersion || createVersion.isPending;

  function handleBrowse() {
    setOpen(false);
    if (!hasConversation || typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('arcanum:open-versions', {
          detail: { conversationId },
        })
      );
    } catch {
      // Defensive — CustomEvent unsupported envs shouldn't crash the page.
    }
  }

  function handleCreate() {
    if (createDisabled) return;
    createVersion.mutate(
      {},
      {
        onSuccess: () => setOpen(false),
      }
    );
  }

  const createErrorMessage =
    createVersion.isError && createVersion.error
      ? createVersion.error.message
      : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="manage-versions-button"
        disabled={!hasConversation}
        onClick={() => hasConversation && setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={
          hasConversation
            ? 'Browse, create, or accept model versions for this deal.'
            : 'Open a deal to manage versions.'
        }
        className={clsx(
          'flex items-center gap-1 px-2 py-1 border rounded-[2px] text-micro font-mono uppercase tracking-[0.5px] transition-colors',
          !hasConversation
            ? 'border-panel-border bg-panel-bg text-text-dim cursor-not-allowed'
            : 'border-panel-border bg-panel-bg text-text-muted hover:border-accent/30 hover:text-text cursor-pointer'
        )}
      >
        <span aria-hidden="true">⊞</span>
        <span>Manage Versions</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && hasConversation && (
        <div
          role="menu"
          data-testid="manage-versions-menu"
          className="absolute right-0 mt-1 z-40 min-w-[230px] border border-panel-border bg-panel-bg rounded-[2px] shadow-[0_4px_16px_rgba(0,0,0,0.5)] py-1"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="manage-versions-browse"
            onClick={handleBrowse}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-mono tracking-[0.5px] text-text-muted hover:bg-accent/10 hover:text-text cursor-pointer transition-colors"
          >
            <span aria-hidden="true">⊞</span>
            <span>Browse versions</span>
          </button>

          <button
            type="button"
            role="menuitem"
            data-testid="manage-versions-create"
            disabled={createDisabled}
            onClick={handleCreate}
            title={
              !canCreateVersion
                ? blockReason
                : 'Save the current model state as a named version.'
            }
            className={clsx(
              'flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-mono tracking-[0.5px] transition-colors',
              createDisabled
                ? 'text-text-dim cursor-not-allowed'
                : 'text-accent hover:bg-accent/10 cursor-pointer'
            )}
          >
            <span aria-hidden="true">+</span>
            <span>
              {createVersion.isPending ? 'Creating version…' : 'Create version'}
            </span>
          </button>

          <AcceptUnderwritingButton
            conversationId={conversationId}
            versionReady={versionReady}
            blockingReason={blockReason}
            menuItem
          />

          {createErrorMessage && (
            <div
              data-testid="manage-versions-create-error"
              className="px-3 py-2 text-[10px] font-mono text-danger"
            >
              {createErrorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
